import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import { Celebration, Person } from '@mui/icons-material';
import { updateUserProfile } from '../services/userService';
import { validateUsername } from '../utils/usernameUtils';

const WelcomeDialog = ({ open, userProfile, onClose, onUpdate }) => {
  const [customUsername, setCustomUsername] = useState('');
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (userProfile?.username) {
      setCustomUsername(userProfile.username);
    }
  }, [userProfile]);

  const handleKeepUsername = async () => {
    setIsUpdating(true);
    try {
      await updateUserProfile(userProfile.uid, { isFirstLogin: false });
      onUpdate({ isFirstLogin: false });
      onClose();
    } catch (error) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCustomUsername = async () => {
    const validation = validateUsername(customUsername);
    
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      await updateUserProfile(userProfile.uid, {
        username: customUsername,
        isFirstLogin: false
      });
      onUpdate({ username: customUsername, isFirstLogin: false });
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to update username. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!userProfile) return null;

  return (
    <Dialog 
      open={open} 
      onClose={!isUpdating ? onClose : undefined}
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={isUpdating}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Celebration color="primary" />
          <Typography variant="h5" component="span">
            Welcome to Form-Pulse!
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Person sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Hi {userProfile.displayName || 'there'}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We've generated a unique username for you:
          </Typography>
        </Box>

        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
            @{userProfile.username}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can keep this username or choose a custom one:
        </Typography>

        <TextField
          fullWidth
          label="Custom Username"
          value={customUsername}
          onChange={(e) => setCustomUsername(e.target.value)}
          disabled={isUpdating}
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1 }}>@</Typography>,
          }}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          You can always change your username later in your profile settings.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
        <Button
          onClick={handleKeepUsername}
          disabled={isUpdating}
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          {isUpdating ? <CircularProgress size={20} /> : 'Keep Current'}
        </Button>
        <Button
          onClick={handleCustomUsername}
          disabled={isUpdating || customUsername === userProfile.username}
          variant="contained"
          sx={{ minWidth: 120 }}
        >
          {isUpdating ? <CircularProgress size={20} /> : 'Use Custom'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeDialog;
